// Live processing status panel. Subscribed via the realtime hook upstream;
// this component is purely presentational based on `processing_state` +
// `processing_percent` from the episode row.

import { ProgressBar, Spinner } from '@ohear/ui'
import type { EpisodeProcessingState } from '../../lib/api/producer-types'

const STAGE_LABELS: Record<EpisodeProcessingState, string> = {
  awaiting_upload: 'Waiting for files',
  validating: 'Reading file metadata (ffprobe)',
  queued: 'Queued for processing',
  slicing: 'Slicing video into HLS segments',
  aligning: 'Aligning script with WhisperX',
  predicting: 'Predicting loudness categories',
  ready: 'Ready for review',
  failed: 'Processing failed — please retry',
}

const TONE: Record<EpisodeProcessingState, 'accent' | 'green' | 'amber' | 'red'> = {
  awaiting_upload: 'amber',
  validating: 'accent',
  queued: 'accent',
  slicing: 'accent',
  aligning: 'accent',
  predicting: 'accent',
  ready: 'green',
  failed: 'red',
}

export interface ProcessingStatusProps {
  state: EpisodeProcessingState
  percent: number | null
}

export function ProcessingStatus({ state, percent }: ProcessingStatusProps) {
  const label = STAGE_LABELS[state]
  const tone = TONE[state]
  const value = state === 'ready' ? 100 : (percent ?? undefined)
  const isActive =
    state === 'validating' ||
    state === 'queued' ||
    state === 'slicing' ||
    state === 'aligning' ||
    state === 'predicting'
  return (
    <div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg-2)] px-4 py-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 text-[14px] font-medium text-[var(--text)]">
          {isActive ? <Spinner size="sm" /> : null}
          {label}
        </span>
        {percent != null && state !== 'ready' && state !== 'failed' ? (
          <span className="text-[12px] text-[var(--text-2)] tabular-nums">
            {Math.round(percent)}%
          </span>
        ) : null}
      </div>
      <ProgressBar value={value} tone={tone} label={`Processing: ${label}`} size="md" />
    </div>
  )
}
